import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BackButton } from '../components/ui';
import { COLORS } from '../lib/styleGuide';
import { BottomNavigation } from '../components/BottomNavigation';

// Global design system constants
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';

const LegalCenter = () => {
  // All policies for the main view
  const allPolicies = [
    {
      id: 'terms-of-service',
      title: 'Terms of Service',
      description: 'Complete terms governing your use of the Vesta platform, including user rights and responsibilities.',
      slug: 'terms',
      roles: ['buyer', 'seller']
    },
    {
      id: 'terms-service-user-agreement',
      title: 'Terms of Service / User Agreement',
      description: 'Comprehensive user agreement covering all aspects of platform usage and user obligations.',
      slug: 'terms-service-user-agreement',
      roles: ['buyer', 'seller']
    },
    {
      id: 'privacy-policy',
      title: 'Privacy Policy',
      description: 'Full policy covering data collection, use, sharing, rights, and international transfers.',
      slug: 'privacy',
      roles: ['buyer', 'seller']
    },
    {
      id: 'buyer-policy',
      title: 'Buyer Policy',
      description: 'Specific responsibilities and expectations for buyers using the Vesta platform.',
      slug: 'buyer-policy',
      roles: ['buyer']
    },
    {
      id: 'seller-policy',
      title: 'Seller Policy',
      description: 'Comprehensive rules and requirements for food creators, sellers, and service providers.',
      slug: 'seller-policy',
      roles: ['seller']
    },
    {
      id: 'refunds-cancellations-disputes',
      title: 'Refunds, Cancellations & Disputes',
      description: 'Perishable goods rules, timelines, escalation procedures, and dispute resolution.',
      slug: 'refunds-cancellations-disputes',
      roles: ['buyer', 'seller']
    },
    {
      id: 'delivery-pickup-policy',
      title: 'Order, Delivery & Pickup Policy',
      description: 'Local delivery and pickup procedures, safe handling, and proof of handoff requirements.',
      slug: 'delivery-pickup',
      roles: ['buyer', 'seller']
    },
    {
      id: 'community-content-policy',
      title: 'Community & Content Policy',
      description: 'Video standards, reviews, respectful conduct, and community guidelines.',
      slug: 'community-content',
      roles: ['buyer', 'seller']
    },
    {
      id: 'content-community-guidelines',
      title: 'Content & Community Guidelines',
      description: 'Detailed guidelines for content creation, community interaction, and professional standards.',
      slug: 'content-community-guidelines',
      roles: ['buyer', 'seller']
    },
    {
      id: 'food-safety-compliance',
      title: 'Food Safety & Compliance Policy',
      description: 'Cottage food laws, commercial licensing, labeling requirements, allergens, and compliance audits.',
      slug: 'food-safety-compliance',
      roles: ['seller']
    },
    {
      id: 'payment-fee-policy',
      title: 'Payment & Fee Policy',
      description: 'Service fees, payout schedules, deposits, chargebacks, and payment processing.',
      slug: 'payment-fee',
      roles: ['seller']
    },
    {
      id: 'preorder-custom-policy',
      title: 'Preorder & Custom/Made-to-Order Policy',
      description: 'Lead times, deposits, cancellations, force majeure, and custom order management.',
      slug: 'preorder-custom',
      roles: ['buyer', 'seller']
    },
    {
      id: 'catering-inhome-chef-policy',
      title: 'Catering & In-Home Chef Policy',
      description: 'On-site service requirements, venue preparation, cleanup, insurance, and safety protocols.',
      slug: 'catering-inhome-chef',
      roles: ['seller']
    },
    {
      id: 'prohibited-items-activities',
      title: 'Prohibited Items & Activities Policy',
      description: 'Banned foods, labeling violations, unsafe content, and prohibited business practices.',
      slug: 'prohibited-items',
      roles: ['buyer', 'seller']
    },
    {
      id: 'intellectual-property-dmca',
      title: 'Intellectual Property & DMCA Policy',
      description: 'Copyright protection, trademark rights, DMCA procedures, and IP violation reporting.',
      slug: 'intellectual-property-dmca',
      roles: ['buyer', 'seller']
    }
  ];

  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: NEUTRAL_BG }}>
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 text-white px-4 py-2 rounded z-50"
        style={{ backgroundColor: PRIMARY_BROWN }}
      >
        Skip to main content
      </a>
      
      {/* Header */}
      <header style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 100,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '16px 20px' 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <BackButton
              onClick={() => navigate(-1)}
              variant="link"
              size="small"
              color={PRIMARY_BROWN}
              ariaLabel="Back to home"
            />
            <h1 style={{ 
              fontFamily: 'Sora, sans-serif',
              fontWeight: 700,
              fontSize: 20, 
              color: NEUTRAL_TEXT, 
              margin: 0 
            }}>
              Legal Center
            </h1>
          </div>
        </div>
      </header>

      <main id="main-content" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" style={{ paddingTop: '72px' }}>
        {/* Main Title */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
            How can we help?
          </h1>
          <p className="text-xl" style={{ color: ACCENT_GRAY, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
            Choose your role to see the policies that apply to you, or browse all policies below.
          </p>
        </div>

        {/* Role Selection Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-16" role="group" aria-label="Select your role to filter policies">
          {/* Buyer Card */}
          <div className="rounded-2xl p-8 shadow-lg transition-all duration-200" 
               style={{ 
                 backgroundColor: NEUTRAL_WHITE, 
                 fontFamily: 'Montserrat, sans-serif'
               }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>Foodie</h2>
            <p className="mb-6" style={{ color: ACCENT_GRAY, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
              For customers who purchase food or book services.
            </p>
            <Link
              to="/legal/buyer"
              className="block w-full py-3 px-6 rounded-lg font-semibold text-center transition-colors"
              style={{ 
                background: COLORS.PRIMARY_GRADIENT,
                backgroundSize: '200% 200%',
                animation: 'gradientShift 3s ease infinite',
                color: NEUTRAL_WHITE,
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700
              }}
              onMouseEnter={(e) => e.currentTarget.style.animation = 'gradientShift 1s ease infinite'}
              onMouseLeave={(e) => e.currentTarget.style.animation = 'gradientShift 3s ease infinite'}
              aria-label="View buyer policies"
            >
              View Buyer Policies
            </Link>
          </div>

          {/* Seller Card */}
          <div className="rounded-2xl p-8 shadow-lg transition-all duration-200" 
               style={{ 
                 backgroundColor: NEUTRAL_WHITE, 
                 fontFamily: 'Montserrat, sans-serif'
               }}>
            <h2 className="text-2xl font-bold mb-4" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>Food Creator</h2>
            <p className="mb-6" style={{ color: ACCENT_GRAY, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
              For sellers, chefs, and caterers.
            </p>
            <Link
              to="/legal/seller"
              className="block w-full py-3 px-6 rounded-lg font-semibold text-center transition-colors"
              style={{ 
                background: COLORS.PRIMARY_GRADIENT,
                backgroundSize: '200% 200%',
                animation: 'gradientShift 3s ease infinite',
                color: NEUTRAL_WHITE,
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700
              }}
              onMouseEnter={(e) => e.currentTarget.style.animation = 'gradientShift 1s ease infinite'}
              onMouseLeave={(e) => e.currentTarget.style.animation = 'gradientShift 3s ease infinite'}
              aria-label="View seller policies"
            >
              View Seller Policies
            </Link>
          </div>
        </div>

        {/* All Policies Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-6 text-center" style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
            All Legal Policies
          </h2>
          <p className="text-center mb-8" style={{ color: ACCENT_GRAY, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
            Browse all available policies below, or use the role filters above for a focused view.
          </p>
        </div>

        {/* Policy List */}
        <div 
          className="space-y-4 focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-lg p-2" 
          role="region" 
          aria-label="All legal policies list"
          tabIndex={-1}
          style={{ focusRingColor: PRIMARY_BROWN }}
        >
          {allPolicies.map((policy) => (
            <div
              key={policy.id}
              className="rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-200"
              style={{ backgroundColor: NEUTRAL_WHITE }}
            >
              <Link
                to={`/legal/${policy.slug}`}
                className="block group"
                aria-label={`Read full ${policy.title} policy`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold transition-colors duration-200 group-hover:text-orange-600" 
                           style={{ color: NEUTRAL_TEXT, fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
                        {policy.title}
                      </h3>
                      <div className="flex gap-2">
                        {policy.roles.includes('buyer') && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full" 
                                style={{ backgroundColor: `${PRIMARY_BROWN}15`, color: PRIMARY_BROWN, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
                            Buyer
                          </span>
                        )}
                        {policy.roles.includes('seller') && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full" 
                                style={{ backgroundColor: `${PRIMARY_BROWN}15`, color: PRIMARY_BROWN, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
                            Seller
                          </span>
                        )}
                      </div>
                    </div>
                    <p style={{ color: ACCENT_GRAY, fontFamily: 'Montserrat, sans-serif', fontWeight: 500 }}>
                      {policy.description}
                    </p>
                  </div>
                  <div className="ml-4 transition-colors duration-200 group-hover:text-orange-500" 
                       style={{ color: ACCENT_GRAY }} aria-hidden="true">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </main>
      
      <BottomNavigation />
    </div>
  );
};

export default LegalCenter; 